// How many times a side's factory runs on each of its moves.
//
// `titan.ts`'s twin: that one answers how many *moves* a turn holds, this one
// how many *ticks* a move buys. Both are things one side owns, so both live on
// the run for the reason stated there — a grant written to a field every side
// reads is a grant the enemy also gets, which is how Titan shipped wrong the
// first time. Named for ticks rather than for tempo because tempo is the other
// file's subject.
//
// **Per side rather than one number for the holder**, because the thing that
// grants ticks is a structure an enemy can walk onto and take (§26 A2, F5),
// so which army holds them is a fact about the board rather than about whose
// run it is. A2 went one further than this file anticipated: the Relic's
// count is *derived* from the structures standing (`relicsOf`) rather than
// written here, so the stored array below is inert plumbing until some other
// granter writes it.
//
// Zero everywhere is a board with no relic exactly: no structure, no entry,
// no extra tick.

import type { Side } from "../board";
import type { GameState, RunState } from "../commands";
import type { IndustryState } from "../industry/state";
import { RULES } from "../rules";
import { addTally, tallySide } from "../tally";
import { runExtraTicks, type TickReport } from "../tick";
import { relicsOf } from "./titan";

/**
 * Extra factory ticks this side gets at the end of each of its moves.
 *
 * Two sources added together: the stored per-side grant below (nothing writes
 * it today — it is F4's channel for a future granter), and **Titan Relics
 * standing under the side's control** (§26 A2), derived from the board for
 * `relicsOf`'s reason — a capture wrecks the structure and ends the grant with
 * it, and no event has to fire.
 */
export function extraTicksFor(
  run: RunState | undefined,
  industry: IndustryState,
  side: Side
): number {
  return (run?.ticksPerSide?.[side] ?? 0) + relicsOf(industry, side) * RULES.relicTicks;
}

/**
 * A run with `delta` more (or fewer) ticks for one side.
 *
 * **The only writer, so it is the one that owns the shape**: dense up to the
 * side it names, never negative, and dropped entirely at nought — so a relic
 * that came and went hashes as one that never landed, and every reader can
 * simply add the number up.
 *
 * **Returns a new run and a new array**, never a write into the old one:
 * `cloneState` shares `run` by reference, so mutating it edits the previous
 * state — a replay would then read a grant that had not been made yet.
 */
export function withExtraTicks(run: RunState, side: Side, delta: number): RunState {
  const ticks = [...(run.ticksPerSide ?? [])];
  while (ticks.length <= side) ticks.push(0);
  ticks[side] = Math.max(0, ticks[side] + delta);
  // **Trailing noughts are trimmed, or two runs holding the same grants hash
  // differently.** Dropping the field only when *every* entry is nought is not
  // enough: a grant taken off side 2 leaves `[1,0,0]` where a run that never
  // gave side 2 one carries `[1]`, and those are the same board. (The Relic
  // itself never writes here — A2 derived its count from the board instead —
  // so this array is the channel for whatever grants ticks next.)
  while (ticks.length > 0 && ticks[ticks.length - 1] === 0) ticks.pop();
  return { ...run, ticksPerSide: ticks.length > 0 ? ticks : undefined };
}

/**
 * A side's factory ticks per turn (§31 T3): the base stat plus every grant.
 *
 * One follows each move and End Turn makes up the rest, at least one — so the
 * extra ticks `extraTicksFor` names are no longer "per move" but per turn.
 */
export function ticksPerTurnFor(
  run: RunState | undefined,
  industry: IndustryState,
  side: Side
): number {
  return RULES.ticksPerTurn + extraTicksFor(run, industry, side);
}

/** Off-Turn Production points this side holds (§31 T3). Nought on every board today. */
export function offTurnFor(run: RunState | undefined, side: Side): number {
  return run?.offTurnPerSide?.[side] ?? 0;
}

/** A run with `delta` more (or fewer) Off-Turn Production points for one side — `withExtraTicks`' shape. */
export function withOffTurn(run: RunState, side: Side, delta: number): RunState {
  const points = [...(run.offTurnPerSide ?? [])];
  while (points.length <= side) points.push(0);
  points[side] = Math.max(0, points[side] + delta);
  while (points.length > 0 && points[points.length - 1] === 0) points.pop();
  return { ...run, offTurnPerSide: points.length > 0 ? points : undefined };
}

/** One-shot Off-Turn Production points this side holds for the next rollover (§31 C4). */
export function offTurnOnceFor(run: RunState | undefined, side: Side): number {
  return run?.offTurnOnce?.[side] ?? 0;
}

/** A run with `delta` more one-shot points for one side — `withOffTurn`'s shape. */
export function withOffTurnOnce(run: RunState, side: Side, delta: number): RunState {
  const points = [...(run.offTurnOnce ?? [])];
  while (points.length <= side) points.push(0);
  points[side] = Math.max(0, points[side] + delta);
  while (points.length > 0 && points[points.length - 1] === 0) points.pop();
  return { ...run, offTurnOnce: points.length > 0 ? points : undefined };
}

/**
 * Run one side's factory `count` times now, and tally what it dug (§26 C2).
 *
 * The one-off twin of `ticksPerSide`: a card is spent, so it cannot write a
 * standing grant. The tally is why this is not a bare `runExtraTicks` — a
 * `collect` objective counts ore out of the ground however many ticks dug it.
 * The returned report is discarded by callers that spend the turn, since
 * `endTurn` assigns its own; that animation gap is §26's, not this function's.
 */
export function spendTicks(state: GameState, side: Side, count: number): TickReport {
  const report = runExtraTicks(state.position, state.industry, side, count);
  if (report.mined > 0 && side === tallySide(state.run)) {
    state.tally = addTally(state.tally, { mined: report.mined });
  }
  return report;
}
